/**
 * Permission helper functions (adapted from api permissionHelpers.js)
 * Uses account.permissions which are already resolved (role + custom) by the backend.
 */

const CREATE = 1 // 1 << 0
const READ = 2 // 1 << 1
const UPDATE = 4 // 1 << 2
const DELETE = 8 // 1 << 3
const ADMIN = 16 // 1 << 4

/**
 * Check if the user has a specific permission for a resource.
 *
 * @param {Object} user - Current user object from getCurrentUser (includes account.permissions)
 * @param {string} resource - Resource key (e.g. 'USERS', 'ACCOUNTS', 'CONSUMERS')
 * @param {string} action - 'CREATE' | 'READ' | 'UPDATE' | 'DELETE' | 'ADMIN'
 * @returns {boolean}
 */
export const hasPermission = (user, resource, action) => {
    if (!user) return false
    if (user.isSuperUser) return true

    const accountPermissions = user.account?.permissions
    if (!accountPermissions || accountPermissions[resource] === undefined) return false

    const permValue = accountPermissions[resource]

    let actionBit
    switch (action) {
        case 'CREATE':
            actionBit = CREATE
            break
        case 'READ':
            actionBit = READ
            break
        case 'UPDATE':
            actionBit = UPDATE
            break
        case 'DELETE':
            actionBit = DELETE
            break
        case 'ADMIN':
            actionBit = ADMIN
            break
        default:
            return false
    }

    return (permValue & actionBit) === actionBit
}

/**
 * Check if a user has any of the specified permissions.
 *
 * @param {Object} user - User object
 * @param {Array<{resource: string, action: string}>} permissionChecks
 * @returns {boolean}
 */
export const hasAnyPermission = (user, permissionChecks) => {
    if (!user || !Array.isArray(permissionChecks)) return false
    return permissionChecks.some((check) => hasPermission(user, check.resource, check.action))
}

/**
 * Conditionally render children based on user permission.
 *
 * Usage:
 *   <PermissionGuard user={user} resource="INVOICES" action="CREATE">
 *     <button>Create Invoice</button>
 *   </PermissionGuard>
 */
export const PermissionGuard = ({user, resource, action, fallback = null, children}) => {
    if (hasPermission(user, resource, action)) {
        return children
    }
    return fallback
}

/**
 * Hook version of hasPermission.
 *
 * Usage:
 *   const canRead = usePermission(user, 'INVOICES', 'READ')
 */
export const usePermission = (user, resource, action) => {
    return hasPermission(user, resource, action)
}
